import { useMutation } from '@apollo/client/react';
import { ProgressCircle, chakra, Popover, Button, HStack, Box, Text } from '@chakra-ui/react';
import { useContentTrackContext } from '@src/components/sco/Education/ContentTrackContext';
import { CheckIcon } from '@src/components/sco/icons';
import { gql } from '@src/lib/gql';
import { useHover } from '@uidotdev/usehooks';
import React, { useEffect, useState } from 'react';
type ContentTrackDotIndicator = {
  isActive?: boolean;
  contentTrackSectionId?: string;
  contentTrackStepId?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
};
export const ContentTrackDotIndicator = (props: ContentTrackDotIndicator) => {
  const [indicatorRef, isIndicatorHovering] = useHover();
  const [popoverRef, isPopoverHovering] = useHover();
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const trackContext = useContentTrackContext();
  const trackProgress = trackContext.getProgress(props.contentTrackSectionId, props.contentTrackStepId);
  const [isCompleted, setIsCompleted] = useState(trackProgress?.completionAsPercent >= 95);
  const isHovering = isIndicatorHovering || isPopoverHovering;
  const completionAsPercent = isCompleted ? 100 : trackProgress?.completionAsPercent;
  const [upsertView] = useMutation(gql`
    mutation ($input: UpsertViewInput!) {
      upsertView(input: $input) {
        view {
          id
          completionAsPercent
          completedAt
          contentTrackEnrollment {
            id
            progress {
              id
              startedAt
              completedAt
              completionAsPercent
              sectionStatuses {
                id
                startedAt
                completedAt
                completionAsPercent
                stepStatuses {
                  id
                  startedAt
                  completedAt
                  completionAsPercent
                  taskStatuses {
                    id
                    startedAt
                    completedAt
                    completionAsPercent
                  }
                }
              }
            }
          }
        }
      }
    }
  `);
  const onClose = () => {
    setIsMarkingComplete(false);
  };
  useEffect(() => {
    if (!isPopoverHovering) {
      onClose();
    }
  }, [isPopoverHovering]);
  const markComplete = () => {
    onClose();
    try {
      upsertView({
        variables: {
          input: {
            viewableType: props.contentTrackStepId ? 'ContentTrackSection' : 'ContentTrackSection',
            viewableId: props.contentTrackStepId || props.contentTrackSectionId,
            viewableContext: 'override',
            parentType: 'ContentTrackEnrollment',
            parentId: trackContext.contentTrackEnrollment?.contentTrackEnrollmentId,
            completedAt: new Date(),
            completionAsPercent: 100
          }
        }
      });
    } catch (err) {
      alert('An error occurred.');
    }
  };
  useEffect(() => {
    if (trackProgress?.completionAsPercent >= 95) {
      setIsCompleted(true);
    }
  }, [trackProgress?.completionAsPercent]);
  return <chakra.span ref={indicatorRef} cursor={isCompleted ? null : 'pointer'} display={'inline-block'} lineHeight={'0'} zIndex={100} onClick={e => {
    setIsMarkingComplete(!isMarkingComplete);
  }}>
      <Popover.Root open={!isCompleted && isMarkingComplete} positioning={{
      placement: 'right'
    }}>
        <Popover.Trigger asChild>
          {isHovering || isCompleted ? <Box alignItems={'center'} bgColor={isCompleted ? 'cyan' : isHovering ? 'whiteAlpha.800' : 'primaryAlpha.500'} borderRadius={'full'} display={'flex'} height={'1em'} justifyContent={'center'} marginTop={'0.25em'} width={'1em'} borderColor={props.isActive ? 'transparent' : isHovering ? 'whiteAlpha.900' : 'primaryAlpha.500'}>
              <CheckIcon color={'colorPalette.solid'} opacity={1} width={'0.6em'} />
            </Box> : <ProgressCircle.Root colorPalette={isHovering || props.isActive ? 'cyan' : 'cyan'} size={props.size || 'sm'} value={completionAsPercent}>
              <ProgressCircle.Circle>
                <ProgressCircle.Track />
                <ProgressCircle.Range stroke={'colorPalette.solid'} strokeLinecap={'round'} />
              </ProgressCircle.Circle>
            </ProgressCircle.Root>}
        </Popover.Trigger>
        <Popover.Positioner>
          <Popover.Content ref={popoverRef} backgroundColor={'blackAlpha.900'} width={'auto'}>
            <Popover.Arrow bgColor={'blackAlpha.900'} />
            <Popover.Body padding={'0.5em 1em'}>
              <HStack gap={2}>
                <Box paddingRight={'0.25em'}>
                  <Text textStyle={'labelSm'}>Completed?</Text>
                </Box>
                <Button size={'xs'} variant={'outline'} onClick={markComplete}>
                  Yes
                </Button>
                <Button size={'xs'} onClick={onClose}>
                  No
                </Button>
              </HStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>
    </chakra.span>;
};